<template>
  <Dialog v-model:open="open">
    <DialogContent v-if="externalUser?.user" class="max-w-[560px]">
      <DialogHeader>
        <DialogTitle>
          {{ externalUser.user.firstName }} has access in
          {{ externalUser.projects.length }}
          project{{ externalUser.projects.length > 1 ? 's' : '' }}
        </DialogTitle>
      </DialogHeader>
      <div class="px-7 pb-6 pt-5 w-full md:min-w-[450px]">
        <Table>
          <TableBody>
            <TableRow
              v-for="project in externalUser.projects"
              :key="project.id"
              class="border-primary-400"
            >
              <TableCell class="py-4">{{ project.name }}</TableCell>
              <TableCell class="py-4 w-44">
                <Select
                  v-if="canManageMemberships"
                  :default-value="project.role"
                  size="sm"
                  @update:model-value="
                    onSelectUpdate($event, project.hash, externalUser.user.id)
                  "
                >
                  <SelectTrigger class="w-auto">
                    <SelectValue :placeholder="roles[project.role].name" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="(role, roleKey) in roles"
                      :key="roleKey"
                      :value="roleKey"
                      se
                    >
                      {{ role.name }}
                      <Badge v-if="role.billable && currentSubscriptionPlan"
                        >Billable</Badge
                      >
                    </SelectItem>
                  </SelectContent>
                </Select>
                <span v-else>{{ roles[project.role].name }}</span>
              </TableCell>
              <TableCell
                v-if="canDeleteMemberships"
                class="text-right w-[40px] py-4"
              >
                <AlertDialog>
                  <AlertDialogTrigger as-child>
                    <Trash class="stroke-error-400 hover:cursor-pointer" />
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    </AlertDialogHeader>
                    <div class="px-7">
                      <AlertDialogDescription>
                        This action will remove
                        <strong>{{ externalUser.user.firstName }}</strong> from
                        <strong>{{ project.name }}</strong>
                      </AlertDialogDescription>
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel as-child>
                        <Button variant="ghost" :disabled="isRemovePending">
                          Cancel
                        </Button>
                      </AlertDialogCancel>
                      <AlertDialogAction as-child>
                        <Button
                          variant="danger"
                          :disabled="isRemovePending"
                          :loading="isRemovePending"
                          @click="
                            onRemoveConfirm(project.hash, externalUser.user.id)
                          "
                        >
                          Remove
                        </Button>
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
} from '@ui/components/dialog';
import { Trash } from 'lucide-vue-next';
import { Button } from '@ui/components/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@ui/components/alert-dialog';
import { Table, TableBody, TableCell, TableRow } from '@ui/components/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/components/select';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import requests from '@/vuex/requests.js';
import { queries } from '@/queries/index.js';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import { Badge } from '@ui/components/badge';
import { useStore } from 'vuex';

const open = defineModel('open', { type: Boolean, default: false });

const { hasPermission: canManageMemberships } = useProtect({
  permissions: [SYS_PERMISSIONS.SYS_MEMBERSHIPS_MANAGE],
});

const { hasPermission: canDeleteMemberships } = useProtect({
  permissions: [SYS_PERMISSIONS.SYS_MEMBERSHIPS_DELETE],
});

defineProps({
  externalUser: {
    type: Object,
    default: null,
  },
});

const roles = ref({
  editor: { name: 'Editor', billable: true },
  viewer: { name: 'Viewer', billable: false },
});

const store = useStore();
const currentSubscriptionPlan = computed(
  () => store.getters['workspace/getCurrentSubscriptionPlan']
);

const queryClient = useQueryClient();

const { mutate: changeAccess } = useMutation({
  mutationFn: async payload => {
    await requests.projects.changeExternalUserAccess(payload);
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries(
      queries.workspace.externalUsers.queryKey
    );
  },
  onError: e => {
    console.error(e);
  },
});

const { isPending: isRemovePending, mutate: removeExternalUser } = useMutation({
  mutationFn: async payload => {
    await requests.projects.removeExternalUser(payload);
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries(
      queries.workspace.externalUsers.queryKey
    );
  },
  onError: e => {
    console.error(e);
  },
});

function onSelectUpdate(value, hash, userId) {
  changeAccess({
    hash,
    id: userId,
    role: value,
  });
}

function onRemoveConfirm(hash, id) {
  removeExternalUser({
    hash,
    id,
  });
}
</script>
